import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { useConversations } from './useConversations';
import { ConversationSidebar } from './components/ConversationSidebar';
import { NewConversationModal } from './components/NewConversationModal';
import { AddMembersModal } from './components/AddMembersModal';
import { ChatHeader } from './components/ChatHeader';
import GroupChat from '../../../components/Client/GroupChat';
import { COLLECTION_NAMES } from '../types';

export function MessagesPage() {
  const { currentUser } = useAuth();
  const {
    conversations,
    createDirectMessage,
    createGroupChat,
    addMembersToConversation,
    markAsRead,
  } = useConversations(currentUser?.uid);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | undefined
  >();
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const sidebarWidth = useBreakpointValue({ base: '100%', md: '320px', lg: '380px' });
  const showSidebar = useBreakpointValue({ base: false, md: true });

  // Get selected conversation details
  const selectedConversation = conversations.find(
    (conv) => conv.id === selectedConversationId
  );

  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversationId) {
      markAsRead(selectedConversationId);
    }
  }, [selectedConversationId, markAsRead]);

  // Update conversation's lastMessageAt when new messages are added
  // This ensures the conversation moves to the top of the list
  useEffect(() => {
    if (!selectedConversationId) return;

    const messagesQuery = query(
      collection(
        db,
        COLLECTION_NAMES.CONVERSATIONS,
        selectedConversationId,
        COLLECTION_NAMES.CHAT_MESSAGES
      ),
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      async (snapshot) => {
        if (!snapshot.empty) {
          const lastMessage = snapshot.docs[0];
          const lastMessageData = lastMessage.data();
          const messageTimestamp = lastMessageData.timestamp;

          // Update conversation's lastMessageAt and updatedAt
          const conversationRef = doc(db, COLLECTION_NAMES.CONVERSATIONS, selectedConversationId);
          await updateDoc(conversationRef, {
            lastMessageAt: messageTimestamp || serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      },
      (error) => {
        console.error('Error listening to messages for conversation update:', error);
      }
    );

    return () => unsubscribe();
  }, [selectedConversationId]);

  const handleNewConversation = () => {
    setShowNewConversationModal(true);
  };

  const handleConversationCreated = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setShowNewConversationModal(false);
  };

  const handleMembersAdded = (conversationId: string) => {
    // If a different conversation ID is returned, switch to that one (duplicate found)
    if (conversationId !== selectedConversationId) {
      setSelectedConversationId(conversationId);
    }
    setShowAddMembersModal(false);
  };

  const getConversationName = () => {
    if (!selectedConversation) return 'Messages';
    if (selectedConversation.type === 'direct') {
      return selectedConversation.otherUser?.name || 'Direct Message';
    }
    return selectedConversation.name || 'Group Chat';
  };

  return (
    <Flex h="100vh" bg={bgColor} direction={{ base: 'column', md: 'row' }}>
      {/* Sidebar */}
      {showSidebar && (
        <Box
          w={sidebarWidth}
          h="100%"
          borderRightWidth="1px"
          display={{ base: selectedConversationId ? 'none' : 'block', md: 'block' }}
        >
          <ConversationSidebar
            selectedConversationId={selectedConversationId}
            onSelectConversation={setSelectedConversationId}
            onNewConversation={handleNewConversation}
          />
        </Box>
      )}

      {/* Main Content */}
      <Box flex="1" h="100%" display="flex" flexDirection="column">
        {selectedConversationId ? (
          <>
            <ChatHeader
              conversationName={getConversationName()}
              isGroupChat={selectedConversation?.type === 'group'}
              memberCount={selectedConversation?.members.length}
              members={selectedConversation?.membersDetails}
              onAddMembers={
                selectedConversation?.type === 'group'
                  ? () => setShowAddMembersModal(true)
                  : undefined
              }
            />
            <Box flex="1" overflow="hidden">
              <GroupChat
                collectionRef={collection(
                  db,
                  COLLECTION_NAMES.CONVERSATIONS,
                  selectedConversationId,
                  COLLECTION_NAMES.CHAT_MESSAGES
                )}
                collectionName="Conversations"
                parentID={selectedConversationId}
                heading={undefined}
                width="100%"
                fullPageChat={true}
                isNotes={false}
                isNotesForReview={false}
                setNotesForReviewCount={undefined}
                setShowNotesForReviewToast={undefined}
                restrictPosting={false}
                fullPageNotes={false}
                parentDocContent={null}
                isDashboard={false}
                referencedLoan={undefined}
                maxMsgs={undefined}
                fillContainer={false}
              />
            </Box>
          </>
        ) : (
          <Box
            flex="1"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={8}
          >
            <Box textAlign="center">
              <Heading size="lg" mb={2} color="gray.600">
                Select a conversation
              </Heading>
              <Text color="gray.500" mb={4}>
                Choose a conversation from the sidebar or start a new one
              </Text>
              {!showSidebar && (
                <Text fontSize="sm" color="gray.400">
                  Use the menu to view your conversations
                </Text>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* New Conversation Modal */}
      <NewConversationModal
        isOpen={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onConversationCreated={handleConversationCreated}
        createDirectMessage={createDirectMessage}
        createGroupChat={createGroupChat}
      />

      {/* Add Members Modal */}
      {selectedConversation && (
        <AddMembersModal
          isOpen={showAddMembersModal}
          onClose={() => setShowAddMembersModal(false)}
          onMembersAdded={handleMembersAdded}
          conversationId={selectedConversation.id}
          currentMemberIds={selectedConversation.members}
          addMembersToConversation={addMembersToConversation}
        />
      )}
    </Flex>
  );
}

